import React, { Component } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import { AutoSizer } from 'react-virtualized';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import Button from '../../elements/editors/mui/Button';
import SchemaViewer from '../../elements/schema-viewer/SchemaViewer';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import { RESOURCE_TYPE_SCHEMA_SPECS } from 'js/constants/resourceTypes';
import ResizableHorizontalLayout from 'js/components/elements/ResizableHorizontalLayout';

import Typography from 'new/elements/Typography';
import { Stack } from 'new/elements/Stack';
import { TooltipWithTitle } from 'new/elements/TooltipWithTitle';

const SAMPLE_JSON = '[\n  { "ID": 1, "NAME": "A" },\n  { "ID": 2, "NAME": "B" }\n]';

class CreateSchemaSpecPage extends Component {
  state = {
    copied: false,
    localInputValue: null, // Track local input value
  };

  componentDidMount() {
    // Initialize local state with prop value
    this.setState({ localInputValue: this.props.input || '' });
  }

  componentDidUpdate(prevProps) {
    // Sync local state when prop changes externally (e.g., form reset)
    if (prevProps.input !== this.props.input && this.props.input !== this.state.localInputValue) {
      this.setState({ localInputValue: this.props.input || '' });
    }
  }

  handleCopySample = () => {
    navigator.clipboard.writeText('[\n  { "ID": 1, "NAME": "A" },\n  { "ID": 2, "NAME": "B" }\n]');
    this.setState({ copied: true });
    setTimeout(() => this.setState({ copied: false }), 2000);
  };

  _updateSpecName = (text) => {
    this.props.updateFormData(this.props.formType, {
      name: text,
    });
  };

  _updateSpecDescription = (text) => {
    this.props.updateFormData(this.props.formType, {
      description: text,
    });
  };

  _updateInputSample = (code) => {
    // Update local state immediately for responsive UI
    this.setState({ localInputValue: code });
  };

  _handleInputBlur = () => {
    // Update Redux when user finishes editing (loses focus)
    this._updateReduxWithCurrentInput();
  };

  _updateReduxWithCurrentInput = () => {
    if (this.state.localInputValue !== this.props.input) {
      this.props.updateFormData(this.props.formType, {
        input: this.state.localInputValue,
      });
    }
  };

  _handleGenerateTestSpec = () => {
    // Update Redux with current local state for form persistence
    this.props.updateFormData(this.props.formType, {
      input: this.state.localInputValue,
    });

    // Pass the current input value directly to avoid Redux timing issues
    this.props.onGenerateTestSpec(this.state.localInputValue);
  };

  render() {
    const {
      containerHeight,
      onGenerateTestSpec,
      isFetchingOutputSamples,
      isEditMode,
      outputSchema = {},
    } = this.props;
    const {
      id = undefined,
      flatSamples = [],
      data_samples = [],
      flatSchema = undefined,
    } = outputSchema;

    const samplesForDisplay = {
      id,
      lastUpdated: Date.now(),
      samples: flatSamples,
      nestedSamples: data_samples,
    };

    //button should be disabled if the code editor is empty
    const readyToGenerate =
      this.state.localInputValue && this.state.localInputValue.trim() !== '' ? true : false;

    let applyCta = null;

    if (isFetchingOutputSamples) {
      applyCta = (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled
          className="cta-button"
          onClick={this._handleGenerateTestSpec}
        >
          <CircularProgress size={20} />
        </Button>
      );
    } else if (readyToGenerate === false) {
      applyCta = (
        <TooltipWithTitle
          placement="bottom"
          arrow={true}
          title={<div>Input Required</div>}
          description={
            <div style={{ maxWidth: 250 }}>
              Please provide JSON sample data to generate a schema
            </div>
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className="cta-button"
              disabled={true}
              onClick={this._handleGenerateTestSpec}
            >
              Apply
            </Button>
          </span>
        </TooltipWithTitle>
      );
    } else {
      applyCta = (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className="cta-button"
          onClick={this._handleGenerateTestSpec}
        >
          Apply
        </Button>
      );
    }

    return (
      <div
        className="schema-spec-form-content-v2"
        style={{ height: containerHeight, overflowX: 'auto', minWidth: 960 }}
      >
        <div className="basic-info-section">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="bodyDefault" color="textSecondary">
              Name
            </Typography>
            <DebouncedText
              debounceTimeMs={0}
              className="input-field"
              placeholder="Name of Template"
              value={this.props.name ? this.props.name : ''}
              onChange={this._updateSpecName}
            />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, width: '100%' }}>
            <Typography variant="bodyDefault" color="textSecondary">
              Description
            </Typography>
            <DebouncedText
              debounceTimeMs={0}
              className="input-field"
              value={this.props.description ? this.props.description : ''}
              onChange={this._updateSpecDescription}
              sx={{ flex: 1 }}
            />
          </Stack>
        </div>
        <div className="schema-content-section">
          <ResizableHorizontalLayout
            initialSizes={['50%', '50%']}
            minSizes={[300, 300]}
            maxSizes={[null, null]}
          >
            <div
              className="input-panel"
              style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                height: '100%',
                minHeight: 0,
              }}
            >
              <div className="schema-content-header">
                <div className="header-content">Input Samples</div>
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <div className="body-header">
                  <div className="label">
                    {isEditMode
                      ? 'Modify the JSON samples below generating the desired schema.'
                      : 'Start by defining your schema. Paste a JSON array of sample records here.'}
                  </div>
                  <div className="generate-cta">{applyCta}</div>
                </div>
                <div className="editor-and-cta" style={{ flex: 1, minHeight: 0 }}>
                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <LoadableMonacoEditor
                      height={'100%'}
                      value={this.state.localInputValue}
                      onChange={this._updateInputSample}
                      onBlur={this._handleInputBlur}
                      theme="light"
                      language="json"
                      options={{
                        minimap: { enabled: false },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="output-panel" style={{ minWidth: 0, height: '100%' }}>
              <div className="schema-content-header">
                <div className="header-content">Generated Schema Template</div>
              </div>
              {!flatSchema || Object.keys(flatSchema).length === 0 ? (
                <div className="output-empty-state">
                  <div className="output-empty-title">No schema defined yet</div>
                  <div className="output-empty-desc">
                    The output preview will appear once you define your schema. Start by pasting a
                    JSON array of records that you want to generate a schema for.
                  </div>
                  <div className="output-empty-sample-instructions subtle">
                    Here's an example of a JSON array you could use:
                  </div>
                  <div className="output-empty-sample-block">
                    <pre className="output-empty-sample-code subtle">{SAMPLE_JSON}</pre>
                    <button
                      className="output-empty-sample-copy subtle"
                      onClick={this.handleCopySample}
                      aria-label="Copy example JSON"
                    >
                      {this.state.copied
                        ? 'Copied! Paste into Input Samples Section'
                        : 'Copy example'}
                    </button>
                  </div>
                </div>
              ) : (
                <SchemaViewer
                  schema={flatSchema || {}}
                  samples={samplesForDisplay?.samples}
                  nestedSamples={samplesForDisplay?.nestedSamples}
                  samplesUpdatedAt={samplesForDisplay?.lastUpdated}
                  isLoadingSamples={isFetchingOutputSamples}
                  resource={outputSchema || {}}
                  resourceType={RESOURCE_TYPE_SCHEMA_SPECS}
                  showCharacteristics={false}
                  hideExpandMetaToggle
                />
              )}
            </div>
          </ResizableHorizontalLayout>
        </div>
      </div>
    );
  }
}

CreateSchemaSpecPage.defaultProps = {};

export default CreateSchemaSpecPage;
